import React, { createContext, useContext, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export type ScoreMode = 'delivery' | 'discovery';

/**
 * Phase-first IA: the lens IS the route. Discovery lives at `/discovery` (and its
 * leaf `/features/*`); Delivery lives at `/delivery` (and its leaf `/repos/*`).
 * Everything else (Overview `/`, `/calibration`) has no lens — we resolve
 * to `delivery` as a harmless default for any component that still reads `mode`
 * outside a phase route. The old `?mode=` query param + localStorage are gone.
 */
export function modeFromPath(pathname: string): ScoreMode {
  if (pathname.startsWith('/discovery') || pathname.startsWith('/features')) {
    return 'discovery';
  }
  // Typed per-tier routes (FR-1) carry the lens as the last path segment, e.g.
  // `/teams/T103/discovery`. Match it as a whole segment so `/delivery-streams/...`
  // (the tier-2 segment) is not mistaken for the delivery lens. Additive: every
  // path the prefix checks above already handled is unaffected.
  if (/\/discovery(\/|$)/.test(pathname)) {
    return 'discovery';
  }
  if (/\/delivery(\/|$)/.test(pathname)) {
    return 'delivery';
  }
  return 'delivery';
}

/** The phase route a lens lives at (used by setMode to navigate between lenses). */
function phaseRoute(mode: ScoreMode): string {
  return mode === 'discovery' ? '/discovery' : '/delivery';
}

interface ScoreModeContextValue {
  mode: ScoreMode;
  /** Switch lens by navigating to the sibling phase route (no in-page toggle). */
  setMode: (mode: ScoreMode) => void;
}

const ScoreModeContext = createContext<ScoreModeContextValue | null>(null);

export function ScoreModeProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();

  const mode = modeFromPath(location.pathname);

  const setMode = useCallback(
    (newMode: ScoreMode) => {
      navigate(phaseRoute(newMode));
    },
    [navigate],
  );

  const value = useMemo(() => ({ mode, setMode }), [mode, setMode]);

  return <ScoreModeContext.Provider value={value}>{children}</ScoreModeContext.Provider>;
}

export function useScoreMode(): ScoreModeContextValue {
  const ctx = useContext(ScoreModeContext);
  if (!ctx) throw new Error('useScoreMode must be used inside ScoreModeProvider');
  return ctx;
}
